import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Store, Plug, Clock, Package, ArrowRight, ExternalLink } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { getSellerStatus, getMyCatalog } from "../lib/hub";
import { useAuth } from "../context/AuthContext";
import { EXTERNAL_LINKS } from "../config";

export default function SellerDashboard() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      getSellerStatus().then(setStatus).catch(() => setStatus(false));
      getMyCatalog().then(setCatalog).catch(() => {});
    }
  }, [user]);

  if (!loading && !user) return (
    <div><PageHero eyebrow="Seller Dashboard" title="Sign in to continue" />
      <div className="px-4 py-12 text-center"><Link to="/login" className="inline-block bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider" data-testid="sd-login">Sign In</Link></div>
    </div>
  );
  if (!status) return <div className="min-h-[50vh] flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  // Non-seller → guide to application (no duplicate registration)
  if (!status.is_seller) return (
    <div data-testid="seller-dashboard-page">
      <PageHero eyebrow="Seller Dashboard" title="Become a TAVEN Seller"
        subtitle="You don't have a seller account yet. Apply to sell on TAVEN — powered by our installed marketplace." />
      <div className="px-4 py-12 text-center" data-testid="sd-not-seller">
        <Store className="w-10 h-10 text-taven-gold mx-auto mb-4" />
        <Link to="/become-a-seller" className="inline-flex items-center gap-1 bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="sd-apply">Start Seller Application <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );

  const ent = status.entitlements; const usage = status.usage;

  return (
    <div data-testid="seller-dashboard-page">
      <PageHero eyebrow="Seller Dashboard" title="Your Seller Hub"
        subtitle="Manage your TAVEN seller presence. Your seller account is a single identity across the marketplace." />
      <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-5xl py-10">

        {/* Connection status */}
        {status.seller_dashboard_state === "connected" ? (
          <div className="border border-green-200 bg-green-50 p-5 mb-6" data-testid="sd-connected">
            <p className="flex items-center gap-2 font-bold text-green-800"><Plug className="w-5 h-5" /> Seller account connected</p>
            <p className="text-sm text-green-700 mt-1">Vendor ID: {status.native_vendor_id}</p>
            <a href={EXTERNAL_LINKS.SELLER_DASHBOARD_URL} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 mt-3 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="sd-open-dashboard">Open Seller Dashboard <ExternalLink className="w-3.5 h-3.5" /></a>
          </div>
        ) : (
          <div className="border border-amber-200 bg-amber-50 p-5 mb-6" data-testid="sd-awaiting">
            <p className="flex items-center gap-2 font-bold text-amber-800"><Clock className="w-5 h-5" /> Seller connection pending</p>
            <p className="text-sm text-amber-700 mt-1">
              Your TAVEN seller identity is set up. The connection to our marketplace system is being finalized —
              you'll get full dashboard access once it's active. Your account, store and products are preserved.
            </p>
          </div>
        )}

        {/* Membership + entitlements */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-black/10 bg-white p-5">
            <h3 className="font-black font-display uppercase text-sm tracking-wide mb-3">Membership & Entitlements</h3>
            <p className="text-sm mb-2">Plan: <strong>{status.membership.plan_name || "None"}</strong> <span className="text-xs text-muted-foreground">({status.membership.status})</span></p>
            <ul className="text-sm space-y-1">
              <li>Dropshipping: <strong>{ent.dropshipping_access ? (ent.dropshipping_tier || "yes") : "no"}</strong></li>
              <li>Wholesale access: <strong>{ent.wholesale_access ? "yes" : "no"}</strong></li>
              <li>Seller-store access: <strong>{ent.seller_store_access ? "yes" : "no"}</strong></li>
              <li>External marketplace: <strong>{ent.external_marketplace_eligible ? "eligible" : "no"}</strong></li>
            </ul>
            <Link to="/my-membership" className="inline-flex items-center gap-1 mt-3 text-xs font-bold uppercase tracking-wider text-taven-black hover:text-taven-gold">Manage membership <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>

          <div className="border border-black/10 bg-white p-5">
            <h3 className="font-black font-display uppercase text-sm tracking-wide mb-3 flex items-center gap-1"><Package className="w-4 h-4" /> Product Catalog</h3>
            {catalog?.access === false ? (
              <div><p className="text-sm text-muted-foreground mb-3">A dropshipping membership is required to build your catalog.</p>
                <Link to="/membership" className="inline-block bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider" data-testid="sd-catalog-upgrade">Choose Membership</Link></div>
            ) : (
              <>
                <p className="text-sm mb-1" data-testid="sd-catalog-usage">Products: <strong>{catalog?.product_count ?? usage.used}</strong>{usage.unlimited ? " (unlimited)" : ` / ${usage.limit}`}</p>
                {!usage.unlimited && usage.limit != null && (
                  <div className="h-2 bg-black/10 rounded-full overflow-hidden mb-2"><div className="h-full bg-taven-gold" style={{ width: `${Math.min(100, (usage.used / (usage.limit || 1)) * 100)}%` }} /></div>
                )}
                {usage.remaining === 0 && <p className="text-xs text-taven-orange font-bold">Limit reached — upgrade to add more products.</p>}
                <Link to="/dropshipping" className="inline-flex items-center gap-1 mt-2 text-xs font-bold uppercase tracking-wider text-taven-black hover:text-taven-gold">Browse products <ArrowRight className="w-3.5 h-3.5" /></Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
